/**
 * Reading a WMS `GetFeatureInfo` reply.
 *
 * Kystverket's speed-limit layers are `queryable`, and clicking a zone is the
 * only way to learn what the limit actually *is* -- the rendered overlay is
 * colour-coded and carries no numbers at the zoom a captain reads. The service
 * offers `text/html`, `application/vnd.ogc.gml` and `text/plain`; this parses
 * the plain form, which is the one whose shape is stable and obvious:
 *
 *     GetFeatureInfo results:
 *
 *     Layer 'layer_762'
 *       Feature 2262:
 *         gjelderfor = 'Bergen'
 *         gjelderforfartoytype = 'næringsfartøy'
 *         hastighet = '5'
 *         ...
 *
 * Not HTML, because that would mean either injecting a third party's markup
 * into the page or stripping it, and neither is something to do with a
 * regulation a captain is about to rely on. Not GML, because it is XML with
 * namespaces around the same handful of strings.
 *
 * Lives here rather than in `captain-pwa` for the same reason `position.ts` and
 * `datetime.ts` do: it is pure, it parses somebody else's format, and this is
 * where vitest can reach it.
 */

/** One feature the service returned, as raw `key = value` pairs. */
export type FeatureAttributes = Record<string, string>

export interface SpeedZone {
  /** Knots. Null when the service returned the field empty or unparseable --
   *  which must render as "not stated", never as 0. */
  knots: number | null
  /** `næringsfartøy` / `fritidsfartøy`, verbatim. The caller decides how to say
   *  it; this module does not translate a regulatory term. */
  vesselType: string | null
  /** The area the rule names -- usually a municipality. */
  area: string | null
  /** `DD.MM`, as published. Null when not given. */
  validFrom: string | null
  validTo: string | null
  /** True when the period covers the whole calendar year, which is most of
   *  them -- worth collapsing in the UI rather than printing "1 Jan - 31 Dec"
   *  on every zone. */
  allYear: boolean
  regulationName: string | null
  /** Lovdata, usually. Only ever `https:` -- see `safeLink`. */
  regulationUrl: string | null
  legalBasis: string | null
  /** Free text; empty string is normalised to null. */
  otherRules: string | null
  bufferZone: string | null
  /** Everything returned, for anything not modelled above. */
  raw: FeatureAttributes
}

/** `  key = 'value'`, allowing an empty value and a quote inside it. */
const ATTRIBUTE = /^\s*([A-Za-z_][\w.]*)\s*=\s*'([\s\S]*?)'\s*$/

/** `  Feature 2262:` -- starts a new record. */
const FEATURE = /^\s*Feature\s+\S+\s*:\s*$/

const NOTHING_FOUND = /search returned no results|no features were found/i

/**
 * Split a `text/plain` GetFeatureInfo body into one attribute map per feature.
 *
 * Returns an empty array for "no results", which is the common case: most of a
 * chart is not inside a speed zone, and a click there is not an error.
 */
export function parseFeatureInfo(body: string): FeatureAttributes[] {
  if (!body || NOTHING_FOUND.test(body)) return []

  const features: FeatureAttributes[] = []
  let current: FeatureAttributes | null = null

  for (const line of body.split(/\r?\n/)) {
    if (FEATURE.test(line)) {
      current = {}
      features.push(current)
      continue
    }
    const match = ATTRIBUTE.exec(line)
    if (!match) continue
    // An attribute before any `Feature` header still belongs to something;
    // keep it rather than dropping data because the layout surprised us.
    if (!current) {
      current = {}
      features.push(current)
    }
    current[match[1]!.toLowerCase()] = match[2]!
  }

  // A feature header with no attributes under it tells a captain nothing.
  return features.filter((f) => Object.keys(f).length > 0)
}

function text(attributes: FeatureAttributes, key: string): string | null {
  const value = attributes[key]?.trim()
  return value ? value : null
}

/** Only `https:` survives. The URL comes from a third party and ends up in an
 *  `href` a captain taps; `javascript:` and `data:` do not belong there. */
export function safeLink(url: string | null): string | null {
  if (!url) return null
  try {
    return new URL(url).protocol === 'https:' ? url : null
  } catch {
    return null
  }
}

/**
 * Interpret one of Kystverket's speed-limit features.
 *
 * `hastighet` is the number a captain came for, so it is the one field that is
 * allowed to be null rather than defaulted: a zone whose limit we could not
 * read has to say so, because rendering a missing value as `0 knots` would be
 * a fabricated instruction.
 */
export function toSpeedZone(attributes: FeatureAttributes): SpeedZone {
  const rawKnots = text(attributes, 'hastighet')
  const parsed = rawKnots === null ? Number.NaN : Number(rawKnots.replace(',', '.'))
  const validFrom = text(attributes, 'periodegyldigfra')
  const validTo = text(attributes, 'periodegyldigtil')

  return {
    knots: Number.isFinite(parsed) && parsed >= 0 ? parsed : null,
    vesselType: text(attributes, 'gjelderforfartoytype'),
    area: text(attributes, 'gjelderfor'),
    validFrom,
    validTo,
    allYear: validFrom === '01.01' && validTo === '31.12',
    regulationName: text(attributes, 'forskriftnavn'),
    regulationUrl: safeLink(text(attributes, 'forskriftlenke')),
    legalBasis: text(attributes, 'lovhjemmel'),
    otherRules: text(attributes, 'annen_regulering'),
    // The service writes "Ingen sone angitt" for "none", which is not a buffer.
    bufferZone: (() => {
      const value = text(attributes, 'buffersone_meter')
      return value && !/^ingen/i.test(value) ? value : null
    })(),
    raw: attributes,
  }
}

/** `01.01` -> `1 Jan`, for a period a captain reads. Passes anything it does
 *  not recognise straight through rather than inventing a date. */
export function formatDayMonth(value: string | null): string | null {
  if (!value) return null
  const match = /^(\d{1,2})\.(\d{1,2})$/.exec(value.trim())
  if (!match) return value
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ]
  const month = months[Number(match[2]) - 1]
  return month ? `${Number(match[1])} ${month}` : value
}
